// MCHD Room Scheduler - User Form Component
// Form component for creating and editing users

import { useEffect } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Select,
  PasswordInput,
  Button,
  Group,
  Alert,
  Text,
  Box,
  Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconCheck,
  IconUser,
  IconMail,
  IconLock,
  IconShield,
} from '@tabler/icons-react';
import { User, RegisterRequest } from '../../../api/endpoints/auth';

interface UserFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (userData: RegisterRequest) => void;
  user?: User | null;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

/**
 * User form component for creating and editing users
 * @param props - Component props
 * @returns JSX element
 */
export function UserForm({
  opened,
  onClose,
  onSubmit,
  user,
  isLoading = false,
  mode,
}: UserFormProps): JSX.Element {
  const form = useForm<RegisterRequest>({
    initialValues: {
      email: '',
      name: '',
      password: '',
      role: 'DOCTOR',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Please enter a valid email address';
        if (!value.endsWith('@moh.gov.sa')) return 'Please use an organization email address';
        return null;
      },
      name: (value) => {
        if (!value) return 'Name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        return null;
      },
      password: (value) => {
        if (mode === 'create' && !value) return 'Password is required';
        if (mode === 'create' && value && value.length < 8) return 'Password must be at least 8 characters';
        return null;
      },
      role: (value) => {
        if (!value) return 'Role is required';
        return null;
      },
    },
  });

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (opened) {
      if (mode === 'edit' && user) {
        form.setValues({
          email: user.email,
          name: user.name,
          password: '', // Don't pre-fill password for security
          role: user.role,
        });
      } else {
        form.reset();
      }
    }
  }, [opened, mode, user]);

  const handleSubmit = (values: RegisterRequest) => {
    // Remove password from edit mode if not provided
    if (mode === 'edit' && !values.password) {
      const { password, ...userData } = values;
      onSubmit(userData as RegisterRequest);
    } else {
      onSubmit(values);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 6) return { strength: 25, label: 'Weak', color: 'red' };
    if (password.length < 8) return { strength: 50, label: 'Fair', color: 'orange' };
    if (password.length < 12) return { strength: 75, label: 'Good', color: 'blue' };
    return { strength: 100, label: 'Strong', color: 'green' };
  };

  const passwordStrength = getPasswordStrength(form.values.password);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'red';
      case 'COORDINATOR':
        return 'blue';
      case 'DOCTOR':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Full system access, user management, security monitoring';
      case 'COORDINATOR':
        return 'Room scheduling, assignment management, coordinator dashboard';
      case 'DOCTOR':
        return 'View personal schedule, room assignments, doctor dashboard';
      default:
        return '';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconUser size={20} />
          <Text fw={600}>
            {mode === 'create' ? 'Create New User' : 'Edit User'}
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* User Information Section */}
          <Box>
            <Text size="sm" fw={500} mb="sm" c="dimmed">
              User Information
            </Text>
            <Stack gap="md">
              <TextInput
                label="Full Name"
                placeholder="Enter full name"
                leftSection={<IconUser size={16} />}
                required
                {...form.getInputProps('name')}
              />

              <TextInput
                label="Email Address"
                placeholder="user@moh.gov.sa"
                leftSection={<IconMail size={16} />}
                required
                {...form.getInputProps('email')}
              />

              <Select
                label="Role"
                placeholder="Select user role"
                leftSection={<IconShield size={16} />}
                data={[
                  { value: 'ADMIN', label: 'Administrator' },
                  { value: 'COORDINATOR', label: 'Coordinator' },
                  { value: 'DOCTOR', label: 'Doctor' },
                ]}
                required
                {...form.getInputProps('role')}
              />

              {/* Role Description */}
              {form.values.role && (
                <Alert
                  icon={<IconShield size={16} />}
                  color={getRoleColor(form.values.role)}
                  variant="light"
                  radius="md"
                >
                  <Text size="sm" fw={500} mb="xs">
                    {form.values.role} Permissions:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {getRoleDescription(form.values.role)}
                  </Text>
                </Alert>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Password Section */}
          <Box>
            <Text size="sm" fw={500} mb="sm" c="dimmed">
              Password
            </Text>
            <Stack gap="md">
              <PasswordInput
                label={mode === 'create' ? 'Password' : 'New Password (optional)'}
                placeholder={mode === 'create' ? 'Enter password' : 'Leave blank to keep current password'}
                leftSection={<IconLock size={16} />}
                required={mode === 'create'}
                {...form.getInputProps('password')}
              />

              {/* Password Strength Indicator */}
              {form.values.password && mode === 'create' && (
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="xs" c="dimmed" fw={500}>
                      Password Strength
                    </Text>
                    <Text size="xs" c={passwordStrength.color} fw={600}>
                      {passwordStrength.label}
                    </Text>
                  </Group>
                  <Box
                    style={{
                      height: 4,
                      backgroundColor: 'var(--mantine-color-gray-2)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      style={{
                        height: '100%',
                        width: `${passwordStrength.strength}%`,
                        backgroundColor: `var(--mantine-color-${passwordStrength.color}-6)`,
                        transition: 'all 0.3s ease',
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Password Requirements */}
              {mode === 'create' && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="blue"
                  variant="light"
                  radius="md"
                >
                  <Text size="xs" fw={500} mb="xs">
                    Password Requirements:
                  </Text>
                  <Text size="xs" c="dimmed">
                    • At least 8 characters long
                    <br />
                    • Must use organization email (@moh.gov.sa)
                    <br />
                    • No password reset functionality (security requirement)
                  </Text>
                </Alert>
              )}
            </Stack>
          </Box>

          {/* Form Actions */}
          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconCheck size={16} />}
              loading={isLoading}
            >
              {mode === 'create' ? 'Create User' : 'Update User'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
